import React from 'react';
import { Button, Empty, Table, Typography, Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
  ACTIVE,
  BUY,
  CALL_OPTION,
  OPTION_CONTRACTS_TYPES,
  PUT_OPTION,
  REGIONS,
  RISK_APPROACHES,
  selectors,
  SELL,
  TRANSACTION_TYPES,
} from 'state';
import { useDispatch, useSelector } from 'react-redux';
import type { TableColumnsType as ColumnsType } from 'antd';
import { OptionsChainRow } from './OptionsChain';
import data from '../../AdminDashboard/Data';
import {
  optionsInstruments,
  optionsInstrumentsList,
} from '../../../state/app/selectors';
import { formatters } from 'services';
import {
  OptionsInstrumentType,
  PlayerOptionsContractType,
} from '../../../state/app/options/types';
import {
  earlyCloseOptionsContract,
  earlyExerciseOptionsContract,
} from '../../../state/app/options/actions';
import { LoadingScreen } from 'components';

type EnrichedPlayerOptionsContractType = PlayerOptionsContractType & {
  js_pnl: number;
  js_intrinsic_pnl: number;
  js_delta_hedge_pnl: number;
  js_overall_pnl: number;
};

const OptionsPositions: React.FC = () => {
  const dispatch = useDispatch();
  const player = useSelector(selectors.player);
  const optionInstruments = useSelector(selectors.optionsInstruments);
  const assets = useSelector(selectors.underlyingAssets);
  const currencies = useSelector(selectors.currencies);
  const session = useSelector(selectors.activeSession);
  const optionsInstrumentsFetching = useSelector(
    selectors.optionsInstrumentsFetching
  );

  if (optionsInstrumentsFetching) {
    return <LoadingScreen />;
  }

  const data = Object.values(optionInstruments).map(instrument => {
    const option_asset = assets[instrument.options_asset]; // not deduped, might be a problem
    const totalPremium = Object.values(instrument.options_contracts)
      .filter(item => item.status === ACTIVE)
      .reduce((sum, row) => {
        return sum + row.premium;
      }, 0);
    return {
      ...instrument,
      name: option_asset.name,
      ccy: currencies[option_asset.base_currency].symbol,
      region: option_asset.region,
      total_premiums: totalPremium,
    };
  });
  const columns: ColumnsType<OptionsInstrumentType> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (region: keyof typeof REGIONS) => REGIONS[region],
      width: 120,
    },
    {
      title: 'CCY',
      dataIndex: 'ccy',
      key: 'ccy',
      width: 50,
    },
    {
      title: 'Total Premiums',
      dataIndex: 'total_premiums',
      key: 'total_premiums',
      render: formatters.commasNoDigits,
    },
  ];

  const expandedRowRender = (record: OptionsInstrumentType) => {
    const deduped = (() => {
      const seen = new Map<number, PlayerOptionsContractType>();
      for (const c of Object.values(record.options_contracts)) {
        if (!seen.has(c.id)) {
          seen.set(c.id, c);
        }
      }
      return Array.from(seen.values());
    })();
    const activeContracts = deduped.filter(item => item.status === ACTIVE);
    const sortedContracts = activeContracts.sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      return tb - ta;
    });

    const optionsAsset = assets[record.options_asset];
    const underlyingAsset = assets[optionsAsset.non_derivate_asset];
    const bid = underlyingAsset.bid;
    const ask = underlyingAsset.ask;

    const enrichedContracts = sortedContracts.map(contract => {
      // PNL (current_premium - open_premium)
      let pnl = 0;
      const open_premium = contract.open_price * contract.contracts * 100;
      let current_premium = 0;
      if (contract.transaction === BUY) {
        // you bought the option, so you have to close by selling @ contract bid
        current_premium = contract.current_bid * contract.contracts * 100;
        pnl = current_premium - open_premium;
      } else {
        // you sold the option, so you have to close by buying @ contract ask
        current_premium = contract.current_ask * contract.contracts * 100;
        pnl = open_premium - current_premium;
      }

      // INTRINSIC PNL

      const strike = contract.strike;
      const amount = contract.contracts * 100;
      let intrinsic_pnl = 0;
      if (
        contract.transaction === BUY &&
        contract.contract_type === CALL_OPTION
      ) {
        // buy call: right to buy at strike, sell @ spot bid in markets
        intrinsic_pnl = Math.max((bid - strike) * amount, 0);
      } else if (
        contract.transaction === BUY &&
        contract.contract_type === PUT_OPTION
      ) {
        // buy put: right to sell at strike, buy @ spot ask in markets
        intrinsic_pnl = Math.max((strike - ask) * amount, 0);
      } else if (
        contract.transaction === SELL &&
        contract.contract_type === CALL_OPTION
      ) {
        // sell call: you have to sell at strike, so you buy @ spot ask in markets
        intrinsic_pnl = Math.min((strike - ask) * amount, 0);
      } else if (
        // sell put: you have to buy at strike, so you sell @ spot bid in markets
        contract.transaction === SELL &&
        contract.contract_type === PUT_OPTION
      ) {
        intrinsic_pnl = Math.min((bid - strike) * amount, 0);
      }
      const js_delta_hedge_pnl = 0;
      const overall_pnl = contract.premium + pnl + js_delta_hedge_pnl;

      return {
        ...contract,
        js_pnl: pnl,
        js_intrinsic_pnl: intrinsic_pnl,
        js_delta_hedge_pnl: js_delta_hedge_pnl,
        js_overall_pnl: overall_pnl,
      };
    });

    const columns = [
      {
        title: 'Txn',
        dataIndex: 'transaction',
        key: 'transaction',
        render: (val: number) => TRANSACTION_TYPES[val],
        width: 15,
      },
      {
        title: 'Type',
        dataIndex: 'contract_type',
        key: 'contract_type',
        render: (val: number) => OPTION_CONTRACTS_TYPES[val],
        width: 20,
      },
      {
        title: 'Maturity',
        dataIndex: 'quote_date',
        key: 'quote_date',
        render: (val: string, row: PlayerOptionsContractType) => {
          const scenarioDate = moment(session.scenario_date, 'YYYY-MM-DD');
          const expirationDate = moment(row.expiration_date, 'YYYY-MM-DD');

          // diff in days (expirationDate - quoteDate)
          return expirationDate.diff(scenarioDate, 'days');
        },
        width: 30,
      },
      { title: 'Strike', dataIndex: 'strike', key: 'strike', width: 25 },
      {
        title: 'Premium',
        dataIndex: 'premium',
        key: 'premium',
        width: 40,
        render: formatters.commasNoDigits,
      },
      {
        title: 'Bid',
        dataIndex: 'current_bid',
        key: 'bid',
        width: 25,
        render: formatters.commas2Digits,
      },
      {
        title: 'Ask',
        dataIndex: 'current_ask',
        key: 'ask',
        width: 25,
        render: formatters.commas2Digits,
      },
      {
        title: 'Open',
        dataIndex: 'open_price',
        key: 'open_price',
        width: 25,
        render: formatters.commas2Digits,
      },
      {
        title: 'Contracts',
        dataIndex: 'contracts',
        key: 'contracts',
        render: (val: number, row: PlayerOptionsContractType) => {
          if (row.transaction === BUY) return formatters.commasNoDigits(val);
          return formatters.commasNoDigits(-val);
        },
        width: 40,
      },
      {
        title: 'Equity eq',
        dataIndex: 'contracts',
        key: 'equity_eq',
        render: (val: number, row: PlayerOptionsContractType) => {
          console.log(val);
          if (row.transaction === BUY)
            return formatters.commasNoDigits(val * 100);
          return formatters.commasNoDigits(-val * 100);
        },
        width: 40,
      },
      {
        title: 'IV Open',
        dataIndex: 'open_iv',
        key: 'open_iv',
        width: 27,
        render: formatters.percent2Digit,
      },
      {
        title: 'IV current',
        dataIndex: 'current_iv',
        key: 'current_iv',
        width: 30,
        render: formatters.percent2Digit,
      },
      {
        title: 'PnL',
        dataIndex: 'js_pnl',
        key: 'js_pnl',
        render: (val: number, row: PlayerOptionsContractType) => {
          return formatters.commasNoDigits(val);
        },
        width: 40,
      },
      {
        title: 'Intrinsic Value',
        dataIndex: 'js_intrinsic_pnl',
        key: 'intrinsic_pnl',
        render: (val: number, row: PlayerOptionsContractType) => {
          return formatters.commasNoDigits(val);
        },
        width: 40,
      },
      {
        title: 'Delta (stocks)',
        dataIndex: 'current_delta',
        key: 'delta_stocks',
        render: (val: number, row: PlayerOptionsContractType) => {
          return formatters.commasNoDigits(val * row.contracts * 100);
        },
        width: 40,
      },
      {
        title: 'Delta Hedge',
        dataIndex: 'contracts',
        key: 'delta_hedge',
        render: (val: number, row: PlayerOptionsContractType) => {
          return '-';
        },
        width: 40,
      },
      {
        title: 'Delta Hedge PnL',
        dataIndex: 'js_delta_hedge_pnl',
        key: 'delta_hedge_pnl',
        render: (val: number, row: PlayerOptionsContractType) => {
          return formatters.commasNoDigits(val);
        },
        width: 50,
      },
      {
        title: (
          <span>
            Overall PnL&nbsp;
            <Popover
              placement="top"
              content={
                <div>
                  Overall PnL is a mark-to-market of the Option contract, as if
                  it was covered in the market (bought/sold).
                </div>
              }
              title="Overall PnL Info"
            >
              <InfoCircleOutlined />
            </Popover>
          </span>
        ),
        dataIndex: 'js_overall_pnl',
        key: 'overall_pnl',
        render: (val: number, row: PlayerOptionsContractType) => {
          return formatters.commasNoDigits(val);
        },
        width: 40,
      },
      {
        title: 'Actions',
        dataIndex: 'contracts',
        key: 'actions',
        render: (val: number, row: EnrichedPlayerOptionsContractType) => {
          const intrinsicValue = row.js_intrinsic_pnl;
          let buyOrSell = 'Buy';
          if (row.transaction === BUY) buyOrSell = 'Sell';
          return (
            <span>
              <Button
                style={{ marginRight: 5 }}
                disabled={row.transaction !== BUY || intrinsicValue <= 0}
                onClick={() => {
                  dispatch(
                    earlyExerciseOptionsContract({
                      player_id: player.id,
                      contract_id: row.id,
                      options_instrument_id: row.options_instrument,
                    })
                  );
                }}
              >
                Execise
              </Button>
              <Button
                style={{ marginRight: 5 }}
                onClick={() => {
                  dispatch(
                    earlyCloseOptionsContract({
                      player_id: player.id,
                      contract_id: row.id,
                      options_instrument_id: row.options_instrument,
                    })
                  );
                }}
              >
                {buyOrSell}
              </Button>
              <Button style={{ marginRight: 5 }}>Delta Hedge</Button>
              <Button>Delta Hedge Blotter</Button>
            </span>
          );
        },
        width: 150,
      },
    ];
    const noData = (
      <Empty
        description={
          <Typography.Text type={'secondary'}>
            No active contracts at this moment.
          </Typography.Text>
        }
      />
    );

    const noDataBehaviour = {
      emptyText: noData,
    };
    return (
      <Table
        style={{ padding: '20px' }}
        columns={columns}
        dataSource={enrichedContracts}
        pagination={false}
        rowKey="id"
        scroll={{ y: '300px' }}
        locale={noDataBehaviour}
        // footer={() => <div></div>}
      />
    );
  };

  return (
    <div>
      <Typography.Title level={4}>Options Instruments</Typography.Title>
      <Table
        columns={columns}
        dataSource={data}
        expandedRowRender={expandedRowRender}
        rowKey="id"
        pagination={false}
        bordered
      />
    </div>
  );
};
export default OptionsPositions;
